import React, { useState } from 'react';

interface Section {
    label: string;
    content: React.ReactNode;
}

interface Equipment {
    id: string;
    name: string;
    category: string;
    summary: string;
    sections: Section[];
}

const EQUIPMENT: Equipment[] = [
    {
        id: 'ac',
        name: '에어컨',
        category: '냉방',
        summary: '냉동 사이클을 이용해 실내 열을 흡수하고 외부로 방출',
        sections: [
            {
                label: '냉동 사이클 (4단계)',
                content: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {[
                            { step: '①압축', desc: '압축기가 저압 가스 냉매를 고온·고압 가스로 압축' },
                            { step: '②응축', desc: '응축기(실외기)에서 고압 가스가 열을 방출하며 액체로 변환' },
                            { step: '③팽창', desc: '팽창밸브(EEV/TXV)에서 고압 액체를 저압으로 감압' },
                            { step: '④증발', desc: '증발기(실내기)에서 저압 액체가 열을 흡수하며 기화 → 실내 냉각' },
                        ].map((s) => (
                            <div key={s.step} style={{ display: 'flex', gap: '8px', fontSize: '13px' }}>
                                <span style={{ fontWeight: 700, color: '#1e3a5f', minWidth: '44px', flexShrink: 0 }}>{s.step}</span>
                                <span style={{ color: '#2c2c2c', lineHeight: 1.5 }}>{s.desc}</span>
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                label: '핵심 부품',
                content: (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                        {[
                            { name: '압축기', role: '냉매 가압·순환 동력원' },
                            { name: '응축기', role: '열 방출 (실외기 코일)' },
                            { name: '팽창밸브', role: '압력 조절 (EEV/TXV)' },
                            { name: '증발기', role: '열 흡수 (실내기 코일)' },
                            { name: '4방향밸브', role: '냉방↔난방 모드 전환' },
                            { name: '어큐뮬레이터', role: '액체 냉매 압축기 유입 방지' },
                        ].map((p) => (
                            <div key={p.name} style={{ backgroundColor: '#f4f1ea', padding: '8px 10px', borderRadius: '8px' }}>
                                <p style={{ fontSize: '12px', fontWeight: 700, color: '#1e3a5f' }}>{p.name}</p>
                                <p style={{ fontSize: '11px', color: '#5a5a5a', marginTop: '2px' }}>{p.role}</p>
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                label: '냉방·난방 전환',
                content: (
                    <p style={{ fontSize: '13px', color: '#2c2c2c', lineHeight: 1.6 }}>
                        4방향밸브(4-Way Valve)가 냉매 흐름 방향을 반전시켜 실내기·실외기 역할을 교환합니다.
                        난방 시 실외기가 증발기, 실내기가 응축기로 작동하여 실내로 열을 방출합니다.
                    </p>
                ),
            },
        ],
    },
    {
        id: 'pac',
        name: '항온항습기',
        category: '정밀공조',
        summary: '온도와 습도를 동시에 정밀 제어 — 데이터센터·클린룸 등에 사용',
        sections: [
            {
                label: '일반 에어컨과의 차이',
                content: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {[
                            { item: '온습도 동시 제어', desc: '온도뿐 아니라 상대습도(RH)도 ±3% 이내로 유지' },
                            { item: '재열기 (Reheat)', desc: '과냉각으로 제습 후 재가열해 설정 온도 복원' },
                            { item: '가습기', desc: '습도 부족 시 스팀 또는 초음파로 수분 공급' },
                            { item: '24시간 연속 운전', desc: '고신뢰성 설계, N+1 이중화 구성 일반적' },
                        ].map((s) => (
                            <div key={s.item} style={{ display: 'flex', gap: '8px', fontSize: '13px' }}>
                                <span style={{ fontWeight: 700, color: '#1e3a5f', minWidth: '72px', flexShrink: 0 }}>{s.item}</span>
                                <span style={{ color: '#2c2c2c', lineHeight: 1.5 }}>{s.desc}</span>
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                label: '제어 흐름',
                content: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {[
                            '센서가 온도·습도 측정',
                            '설정값 초과 시 → 압축기 가동 (냉각·제습)',
                            '온도 저하 시 → 재열기 가동 (재가열)',
                            '습도 부족 시 → 가습기 가동',
                            'PID 제어로 연속 보정',
                        ].map((step, i) => (
                            <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '13px', alignItems: 'flex-start' }}>
                                <span style={{ width: '20px', height: '20px', backgroundColor: '#1e3a5f', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                                <span style={{ color: '#2c2c2c', lineHeight: 1.5 }}>{step}</span>
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                label: '주요 점검사항',
                content: (
                    <ul style={{ fontSize: '13px', color: '#2c2c2c', lineHeight: 1.8, paddingLeft: '16px', margin: 0 }}>
                        <li>가습기 스케일(수분 침전물) 주기적 청소</li>
                        <li>재열 히터 전류·온도 정상 여부</li>
                        <li>드레인 팬·배수구 막힘 확인</li>
                        <li>필터 압력차 경보 설정값 점검</li>
                        <li>습도 센서 교정 (연 1회 권장)</li>
                    </ul>
                ),
            },
        ],
    },
    {
        id: 'chiller',
        name: '칠러',
        category: '냉수',
        summary: '냉매로 물을 냉각해 냉수(7°C)를 생성·공급하는 대형 냉동기',
        sections: [
            {
                label: '기본 구성',
                content: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {[
                            { part: '압축기', desc: '원심식·스크루·왕복동 중 선택' },
                            { part: '증발기', desc: '냉수를 냉각 (냉매가 물의 열을 흡수)' },
                            { part: '응축기', desc: '냉각수 또는 외기로 열 방출' },
                            { part: '냉각탑', desc: '수냉식의 경우 냉각수 열을 대기로 방산' },
                        ].map((p) => (
                            <div key={p.part} style={{ display: 'flex', gap: '8px', fontSize: '13px' }}>
                                <span style={{ fontWeight: 700, color: '#1e3a5f', minWidth: '52px', flexShrink: 0 }}>{p.part}</span>
                                <span style={{ color: '#2c2c2c', lineHeight: 1.5 }}>{p.desc}</span>
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                label: '수냉식 vs 공냉식',
                content: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {[
                            { type: '수냉식', pros: '효율 높음 (COP 5~7)', cons: '냉각탑·배관 추가 필요, 수처리 관리 필수' },
                            { type: '공냉식', pros: '설치 간단, 수처리 불필요', cons: '효율 낮음 (COP 3~4), 외기 온도 영향 큼' },
                        ].map((t) => (
                            <div key={t.type} style={{ backgroundColor: '#f4f1ea', padding: '10px 12px', borderRadius: '8px' }}>
                                <p style={{ fontSize: '13px', fontWeight: 700, color: '#1e3a5f', marginBottom: '4px' }}>{t.type}</p>
                                <p style={{ fontSize: '12px', color: '#5a7a6a' }}>▲ {t.pros}</p>
                                <p style={{ fontSize: '12px', color: '#8b4513', marginTop: '2px' }}>▼ {t.cons}</p>
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                label: 'COP (성능계수)',
                content: (
                    <div>
                        <div style={{ backgroundColor: '#1e3a5f', padding: '12px 16px', borderRadius: '8px', textAlign: 'center', marginBottom: '10px' }}>
                            <p style={{ color: 'white', fontSize: '15px', fontWeight: 700, fontFamily: "'Roboto Mono', monospace" }}>
                                COP = 냉동능력(kW) ÷ 소비전력(kW)
                            </p>
                        </div>
                        <p style={{ fontSize: '13px', color: '#2c2c2c', lineHeight: 1.6 }}>
                            COP가 높을수록 효율적. 수냉식은 5~7, 공냉식은 3~4 수준.
                            부분부하(PLR) 운전 시 COP가 변동되므로 IPLV(통합부분부하 성능계수)도 확인합니다.
                        </p>
                    </div>
                ),
            },
            {
                label: '주요 점검사항',
                content: (
                    <ul style={{ fontSize: '13px', color: '#2c2c2c', lineHeight: 1.8, paddingLeft: '16px', margin: 0 }}>
                        <li>냉수 입·출구 온도차 (설계값 5°C 기준)</li>
                        <li>냉각수 수질 관리 (레지오넬라균 주의)</li>
                        <li>냉매 압력·온도 로그 확인</li>
                        <li>오일 레벨 및 오일 히터 작동 확인</li>
                        <li>진동·소음 이상 유무</li>
                    </ul>
                ),
            },
        ],
    },
    {
        id: 'coolingtower',
        name: '냉각탑',
        category: '냉수',
        summary: '칠러 응축기에서 가열된 냉각수를 외기로 냉각하는 설비',
        sections: [
            {
                label: '냉각 원리',
                content: (
                    <p style={{ fontSize: '13px', color: '#2c2c2c', lineHeight: 1.6 }}>
                        냉각수 일부가 증발할 때 주변의 열(잠열 약 2,450 kJ/kg)을 흡수합니다.
                        이 증발냉각 원리로 냉각수 온도를 낮춥니다.
                        외기 습구온도에 성능이 크게 의존하며, 일반적으로 냉각수 출구 온도는
                        외기 습구온도보다 3~5°C 높게 설계됩니다.
                    </p>
                ),
            },
            {
                label: '개방형 vs 밀폐형',
                content: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {[
                            { type: '개방형', desc: '냉각수가 외기와 직접 접촉. 효율 높으나 수질 오염·레지오넬라 위험' },
                            { type: '밀폐형', desc: '냉각수는 밀폐 코일 안에서 순환. 수질 유지 용이, 설비비 높음' },
                        ].map((t) => (
                            <div key={t.type} style={{ backgroundColor: '#f4f1ea', padding: '10px 12px', borderRadius: '8px' }}>
                                <p style={{ fontSize: '13px', fontWeight: 700, color: '#1e3a5f', marginBottom: '4px' }}>{t.type}</p>
                                <p style={{ fontSize: '12px', color: '#5a5a5a', lineHeight: 1.5 }}>{t.desc}</p>
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                label: '주요 점검사항',
                content: (
                    <ul style={{ fontSize: '13px', color: '#2c2c2c', lineHeight: 1.8, paddingLeft: '16px', margin: 0 }}>
                        <li>충진재(Fill) 막힘·파손 확인</li>
                        <li>팬 전류·회전수 정상 여부</li>
                        <li>보충수 수질·약품 농도 관리</li>
                        <li>블로우다운(Blowdown) 주기 설정</li>
                        <li>동절기 동파 방지 조치</li>
                    </ul>
                ),
            },
        ],
    },
    {
        id: 'ahu',
        name: '공기조화기 (AHU)',
        category: '공조',
        summary: '필터·코일·가습·송풍기를 통해 대규모 공간의 공기를 조화하는 장치',
        sections: [
            {
                label: '공기 처리 흐름',
                content: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {[
                            { step: '외기 도입', desc: '외기(OA)와 환기(RA)를 혼합' },
                            { step: '필터', desc: '먼지·이물질 제거 (프리필터 → 헤파 등)' },
                            { step: '냉·온수 코일', desc: '칠러/보일러 연결 코일로 냉각 또는 가열' },
                            { step: '가습기', desc: '건구 가습 — 스팀 또는 기화식' },
                            { step: '송풍기', desc: '조화된 공기를 덕트로 공급 (SA)' },
                        ].map((s, i) => (
                            <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '13px', alignItems: 'flex-start' }}>
                                <span style={{ width: '20px', height: '20px', backgroundColor: '#c75c2e', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, flexShrink: 0 }}>{i + 1}</span>
                                <div>
                                    <span style={{ fontWeight: 700, color: '#1e3a5f' }}>{s.step}</span>
                                    <span style={{ color: '#5a5a5a' }}> — {s.desc}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                label: '약어 정리',
                content: (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
                        {[
                            { abbr: 'OA', full: 'Outside Air (외기)' },
                            { abbr: 'RA', full: 'Return Air (환기)' },
                            { abbr: 'SA', full: 'Supply Air (급기)' },
                            { abbr: 'EA', full: 'Exhaust Air (배기)' },
                            { abbr: 'MAT', full: '혼합공기온도' },
                            { abbr: 'AHU', full: 'Air Handling Unit' },
                        ].map((a) => (
                            <div key={a.abbr} style={{ backgroundColor: '#f4f1ea', padding: '6px 10px', borderRadius: '8px' }}>
                                <p style={{ fontSize: '13px', fontWeight: 700, color: '#1e3a5f', fontFamily: "'Roboto Mono', monospace" }}>{a.abbr}</p>
                                <p style={{ fontSize: '11px', color: '#5a5a5a', marginTop: '1px' }}>{a.full}</p>
                            </div>
                        ))}
                    </div>
                ),
            },
            {
                label: '주요 점검사항',
                content: (
                    <ul style={{ fontSize: '13px', color: '#2c2c2c', lineHeight: 1.8, paddingLeft: '16px', margin: 0 }}>
                        <li>필터 차압 확인 (교체 기준 초과 시 교체)</li>
                        <li>코일 핀 청결·부식 여부</li>
                        <li>드레인 팬 배수 원활 여부</li>
                        <li>댐퍼 개폐 및 액추에이터 동작</li>
                        <li>송풍기 벨트 장력·마모 상태</li>
                    </ul>
                ),
            },
        ],
    },
    {
        id: 'heatpump',
        name: '히트펌프',
        category: '냉난방',
        summary: '냉동 사이클을 역이용해 난방을 제공 — 전기히터 대비 3배 이상 효율',
        sections: [
            {
                label: '원리',
                content: (
                    <p style={{ fontSize: '13px', color: '#2c2c2c', lineHeight: 1.6 }}>
                        히트펌프는 저온 열원(외기·지열·수열)에서 열을 흡수하여 고온 측으로 전달합니다.
                        4방향밸브로 냉매 흐름을 반전하면 실외기가 증발기, 실내기가 응축기로 작동해
                        실내로 열을 방출합니다.
                    </p>
                ),
            },
            {
                label: '히트펌프 COP',
                content: (
                    <div>
                        <div style={{ backgroundColor: '#1e3a5f', padding: '12px 16px', borderRadius: '8px', textAlign: 'center', marginBottom: '10px' }}>
                            <p style={{ color: 'white', fontSize: '14px', fontWeight: 700, fontFamily: "'Roboto Mono', monospace" }}>
                                COP(난방) = 난방능력 ÷ 소비전력
                            </p>
                        </div>
                        <p style={{ fontSize: '13px', color: '#2c2c2c', lineHeight: 1.6 }}>
                            외기 온도 7°C 기준 일반적으로 COP 3~4.
                            외기 온도가 낮아질수록 COP 저하 → 혹한기 보조 히터(Aux Heater) 병용.
                        </p>
                    </div>
                ),
            },
            {
                label: '제상 운전 (Defrost)',
                content: (
                    <p style={{ fontSize: '13px', color: '#2c2c2c', lineHeight: 1.6 }}>
                        외기 온도 0°C 이하에서 실외기 코일에 서리(霜)가 착빙됩니다.
                        일정 시간 또는 센서 감지 시 냉방 모드로 전환해 서리를 제거합니다.
                        제상 중에는 난방이 일시 중단되므로 보조 열원 활용을 권장합니다.
                    </p>
                ),
            },
        ],
    },
];

const CATEGORY_COLORS: Record<string, { bg: string; text: string }> = {
    '냉방':   { bg: '#e3f2fd', text: '#1e3a5f' },
    '정밀공조': { bg: '#f3e5f5', text: '#6a1b9a' },
    '냉수':   { bg: '#e8f5e9', text: '#5a7a6a' },
    '공조':   { bg: '#fff8e1', text: '#8b6914' },
    '냉난방': { bg: '#ffedd5', text: '#8b4513' },
};

export const Principles: React.FC = () => {
    const [expandedEquip, setExpandedEquip] = useState<string | null>('ac');
    const [expandedSection, setExpandedSection] = useState<string | null>('ac-0');

    const toggleEquip = (id: string) => {
        if (expandedEquip === id) {
            setExpandedEquip(null);
            setExpandedSection(null);
        } else {
            setExpandedEquip(id);
            setExpandedSection(`${id}-0`);
        }
    };

    const toggleSection = (key: string) => {
        setExpandedSection(expandedSection === key ? null : key);
    };

    return (
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {EQUIPMENT.map((equip) => {
                const isOpen = expandedEquip === equip.id;
                const catColor = CATEGORY_COLORS[equip.category] || { bg: '#f5f5f5', text: '#5a5a5a' };
                return (
                    <div key={equip.id} style={{ backgroundColor: 'white', border: `1px solid ${isOpen ? '#1e3a5f' : '#c4b8a5'}`, borderRadius: '12px', overflow: 'hidden', transition: 'border-color 0.15s' }}>
                        {/* 장비 헤더 */}
                        <button
                            onClick={() => toggleEquip(equip.id)}
                            style={{
                                width: '100%', padding: '14px 16px',
                                backgroundColor: isOpen ? '#1e3a5f' : 'white',
                                border: 'none', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                transition: 'background-color 0.15s',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '13px', fontWeight: 700, color: isOpen ? 'white' : '#2c2c2c' }}>
                                    {equip.name}
                                </span>
                                <span style={{ fontSize: '11px', padding: '2px 8px', backgroundColor: isOpen ? 'rgba(255,255,255,0.2)' : catColor.bg, color: isOpen ? 'white' : catColor.text, borderRadius: '20px', fontWeight: 700 }}>
                                    {equip.category}
                                </span>
                            </div>
                            <span style={{ fontSize: '12px', color: isOpen ? '#a0b4c8' : '#a09890' }}>
                                {isOpen ? '▲' : '▼'}
                            </span>
                        </button>

                        {/* 요약 */}
                        {isOpen && (
                            <div style={{ borderBottom: '1px solid #e8e0d5', padding: '10px 16px', backgroundColor: '#f0f4f8' }}>
                                <p style={{ fontSize: '12px', color: '#5a5a5a', lineHeight: 1.5 }}>{equip.summary}</p>
                            </div>
                        )}

                        {/* 섹션 목록 */}
                        {isOpen && equip.sections.map((section, idx) => {
                            const sectionKey = `${equip.id}-${idx}`;
                            const isSectionOpen = expandedSection === sectionKey;
                            return (
                                <div key={sectionKey} style={{ borderTop: idx === 0 ? 'none' : '1px solid #e8e0d5' }}>
                                    <button
                                        onClick={() => toggleSection(sectionKey)}
                                        style={{
                                            width: '100%', padding: '11px 16px',
                                            backgroundColor: isSectionOpen ? '#f4f1ea' : 'white',
                                            border: 'none', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            transition: 'background-color 0.15s',
                                        }}
                                    >
                                        <span style={{ fontSize: '13px', fontWeight: isSectionOpen ? 700 : 400, color: isSectionOpen ? '#1e3a5f' : '#2c2c2c' }}>
                                            {section.label}
                                        </span>
                                        <span style={{ fontSize: '11px', color: '#a09890' }}>
                                            {isSectionOpen ? '−' : '+'}
                                        </span>
                                    </button>
                                    {isSectionOpen && (
                                        <div style={{ padding: '12px 16px 14px', borderTop: '1px solid #e8e0d5', backgroundColor: 'white' }}>
                                            {section.content}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
};
